/**
 * Admin Log Service
 * Handles logging of all admin actions to the adminActionLogs collection
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import type { AdminActionType } from '@/types/admin';

export interface LogAdminActionInput {
  adminUserId: string;
  actionType: AdminActionType;
  targetUserId?: string;
  targetCaseId?: string;
  targetProUserId?: string;
  targetConfigKey?: string;
  details?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Log an admin action to the adminActionLogs collection
 * 
 * @param input - Admin action details
 * @returns Promise that resolves when the log entry is written
 * 
 * @example
 * ```ts
 * await logAdminAction({
 *   adminUserId: 'admin123',
 *   actionType: 'sudoStart',
 *   targetUserId: 'user456',
 *   details: 'Starting sudo session for debugging'
 * });
 * ```
 */
export async function logAdminAction(input: LogAdminActionInput): Promise<void> {
  try {
    const logEntry = {
      adminUserId: input.adminUserId,
      actionType: input.actionType,
      createdAt: serverTimestamp(),
      ...(input.targetUserId && { targetUserId: input.targetUserId }),
      ...(input.targetCaseId && { targetCaseId: input.targetCaseId }),
      ...(input.targetProUserId && { targetProUserId: input.targetProUserId }),
      ...(input.targetConfigKey && { targetConfigKey: input.targetConfigKey }),
      ...(input.details && { details: input.details }),
      ...(input.metadata && { metadata: input.metadata }),
    };

    await addDoc(collection(db, 'adminActionLogs'), logEntry);
  } catch (error) {
    console.error('[adminLogService] Failed to log admin action:', error);
    // Don't throw - logging failures shouldn't break admin operations
  }
}

/**
 * Log a sudo session start
 */
export async function logSudoStart(adminUserId: string, targetUserId: string, targetEmail?: string): Promise<void> {
  await logAdminAction({
    adminUserId,
    actionType: 'sudoStart',
    targetUserId,
    details: `Started sudo session for ${targetEmail || targetUserId}`,
  });
}

/**
 * Log a sudo session end
 */
export async function logSudoEnd(adminUserId: string, targetUserId: string): Promise<void> {
  await logAdminAction({
    adminUserId,
    actionType: 'sudoEnd',
    targetUserId,
    details: 'Ended sudo session',
  });
}

/**
 * Log a bulk operation execution
 */
export async function logBulkOperation(
  adminUserId: string,
  operationType: string,
  processedCount: number,
  metadata?: Record<string, unknown>
): Promise<void> {
  await logAdminAction({
    adminUserId,
    actionType: 'bulkOperationRun',
    details: `Executed ${operationType}: ${processedCount} items processed`,
    metadata: {
      operationType,
      processedCount,
      ...metadata,
    },
  });
}
